import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Activity, Heart, Zap, Timer } from 'lucide-react';

export const UniversalPerformanceAnalytics: React.FC = () => {
  const { user } = useAuth();

  const { data: performanceData, isLoading } = useQuery({
    queryKey: ['universal-performance', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Get last 30 runs for analysis
      const { data: runs } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .order('recorded_date', { ascending: false })
        .limit(30);

      if (!runs || runs.length === 0) return null;

      // Analyze performance across all data sources
      const stravaRuns = runs.filter(r => r.data_source === 'strava');
      const manualRuns = runs.filter(r => r.data_source === 'manual');
      
      // Calculate trends
      const paceData = runs
        .filter(r => r.pace_per_km_seconds)
        .slice(0, 10)
        .reverse()
        .map((run, index) => ({
          date: new Date(run.recorded_date).toLocaleDateString(),
          pace: Math.round(run.pace_per_km_seconds / 60 * 100) / 100, // Convert to minutes
          source: run.data_source,
          distance: run.distance_meters / 1000
        }));

      // Heart rate analysis
      const hrData = runs
        .filter(r => r.heart_rate_avg)
        .slice(0, 10)
        .reverse()
        .map(run => ({
          date: new Date(run.recorded_date).toLocaleDateString(),
          avgHR: run.heart_rate_avg,
          maxHR: run.heart_rate_max,
          source: run.data_source
        }));

      // Weekly distance analysis
      const weeklyData = runs
        .reduce((acc: any[], run) => {
          const weekStart = new Date(run.recorded_date);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          const weekKey = weekStart.toISOString().split('T')[0];
          
          const existing = acc.find(w => w.week === weekKey);
          if (existing) {
            existing.distance += (run.distance_meters || 0) / 1000;
            existing.runs += 1;
            if (run.data_source === 'strava') existing.stravaRuns += 1;
          } else {
            acc.push({
              week: weekKey,
              weekLabel: weekStart.toLocaleDateString(),
              distance: (run.distance_meters || 0) / 1000,
              runs: 1,
              stravaRuns: run.data_source === 'strava' ? 1 : 0
            });
          }
          return acc;
        }, [])
        .slice(0, 8)
        .reverse();

      // Performance insights
      const insights = [];
      
      // Pace consistency
      if (paceData.length > 3) {
        const recentPaces = paceData.slice(-3).map(d => d.pace);
        const avgRecent = recentPaces.reduce((sum, p) => sum + p, 0) / recentPaces.length;
        const variation = Math.max(...recentPaces) - Math.min(...recentPaces);
        
        if (variation < 0.5) {
          insights.push({
            type: 'pace_consistency',
            message: `Excellent pace consistency - only ${(variation * 60).toFixed(0)}s variation in recent runs`,
            trend: 'positive'
          });
        }
      }

      // Data source analysis
      if (stravaRuns.length > 0 && manualRuns.length > 0) {
        insights.push({
          type: 'data_sources',
          message: `Mixed data tracking: ${stravaRuns.length} Strava activities, ${manualRuns.length} manual entries`,
          trend: 'neutral'
        });
      }

      // Strava-specific insights
      if (stravaRuns.length > 3) {
        const avgSufferScore = stravaRuns
          .filter(r => r.suffer_score)
          .reduce((sum, r) => sum + r.suffer_score, 0) / stravaRuns.filter(r => r.suffer_score).length;
        
        if (avgSufferScore > 0) {
          insights.push({
            type: 'strava_suffer',
            message: `Average Strava suffer score: ${Math.round(avgSufferScore)} - ${avgSufferScore > 80 ? 'high training stress' : avgSufferScore > 50 ? 'moderate training load' : 'manageable training stress'}`,
            trend: avgSufferScore > 80 ? 'negative' : 'neutral'
          });
        }
      }

      return {
        paceData,
        hrData,
        weeklyData,
        insights,
        summary: {
          totalRuns: runs.length,
          stravaRuns: stravaRuns.length,
          manualRuns: manualRuns.length,
          totalDistance: runs.reduce((sum, r) => sum + (r.distance_meters || 0), 0) / 1000,
          avgPace: paceData.length > 0 ? paceData.reduce((sum, d) => sum + d.pace, 0) / paceData.length : 0,
          avgHR: hrData.length > 0 ? hrData.reduce((sum, d) => sum + d.avgHR, 0) / hrData.length : 0
        }
      };
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!performanceData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Start tracking your runs to see performance analytics!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{performanceData.summary.totalRuns}</div>
                <div className="text-xs text-muted-foreground">Total Runs</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-600" />
              <div>
                <div className="text-2xl font-bold">{performanceData.summary.stravaRuns}</div>
                <div className="text-xs text-muted-foreground">Strava Activities</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-green-600" />
              <div>
                <div className="text-2xl font-bold">
                  {performanceData.summary.avgPace > 0 ? `${Math.floor(performanceData.summary.avgPace)}:${Math.round((performanceData.summary.avgPace % 1) * 60).toString().padStart(2, '0')}` : 'N/A'}
                </div>
                <div className="text-xs text-muted-foreground">Avg Pace/km</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-red-600" />
              <div>
                <div className="text-2xl font-bold">
                  {performanceData.summary.avgHR > 0 ? Math.round(performanceData.summary.avgHR) : 'N/A'}
                </div>
                <div className="text-xs text-muted-foreground">Avg Heart Rate</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pace Trend Chart */}
      {performanceData.paceData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pace Trends</CardTitle>
            <CardDescription>Recent pace performance across all data sources</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceData.paceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'Pace (min/km)', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  formatter={(value: any, name: any, props: any) => [
                    `${Math.floor(value)}:${Math.round((value % 1) * 60).toString().padStart(2, '0')}/km`,
                    'Pace'
                  ]}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="pace" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={(props) => (
                    <circle 
                      cx={props.cx} 
                      cy={props.cy} 
                      r={4} 
                      fill={props.payload.source === 'strava' ? '#f97316' : '#6b7280'}
                    />
                  )}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Weekly Distance */}
      {performanceData.weeklyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Weekly Training Volume</CardTitle>
            <CardDescription>Distance and activity count by week</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="weekLabel" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="distance" fill="#3b82f6" name="Distance (km)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {performanceData.insights.map((insight, index) => (
            <div key={index} className="flex items-start gap-3 p-3 rounded-lg border">
              <div className={`p-2 rounded-lg ${
                insight.trend === 'positive' ? 'bg-green-100 text-green-600' : 
                insight.trend === 'negative' ? 'bg-red-100 text-red-600' : 
                'bg-blue-100 text-blue-600'
              }`}>
                {insight.trend === 'positive' ? <TrendingUp className="h-4 w-4" /> : 
                 insight.trend === 'negative' ? <TrendingDown className="h-4 w-4" /> : 
                 <Activity className="h-4 w-4" />}
              </div>
              <div className="flex-1">
                <p className="text-sm">{insight.message}</p>
                <Badge variant="outline" className="mt-1 text-xs">
                  {insight.type.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};